import type {
  CommentResponseDto,
  ListCommentsResponseDto,
  UpdateCommentDto,
} from '@inference.engine/workspace-api-client/web';
import type { InfiniteData } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationService } from '@/lib/services/notification.service';
import { logger } from '@/lib/utils/logger';
import type { CommentTargetRef } from '@/entities/project-comment';
import { isCommentsListKey, PROJECT_COMMENT_QUERY_KEYS } from '@/entities/project-comment';
import { invalidateCommentAnnotations } from './invalidate-comment-annotations';
import { updateComment } from './comments.api';
import { useTranslation } from 'react-i18next';

export { updateComment };

interface UseUpdateCommentOptions {
  projectId: string;
  target: CommentTargetRef;
}

interface UpdateCommentArgs {
  commentId: string;
  body: UpdateCommentDto;
}

function replaceCommentInRoots(updated: CommentResponseDto) {
  return (
    old: InfiniteData<ListCommentsResponseDto, number> | undefined,
  ): InfiniteData<ListCommentsResponseDto, number> | undefined => {
    if (!old) return old;
    const pages: ListCommentsResponseDto[] = [];
    let replaced = false;
    for (const page of old.pages) {
      if (replaced) {
        pages.push(page);
        continue;
      }
      const index = page.items.findIndex((item) => item.id === updated.id);
      if (index === -1) {
        pages.push(page);
        continue;
      }
      const items = [...page.items];
      items[index] = updated;
      pages.push({ ...page, items });
      replaced = true;
    }
    return replaced ? { ...old, pages } : old;
  };
}

function replaceCommentInReplies(updated: CommentResponseDto) {
  return (old: ListCommentsResponseDto | undefined): ListCommentsResponseDto | undefined => {
    if (!old) return old;
    const index = old.items.findIndex((item) => item.id === updated.id);
    if (index === -1) return old;
    const items = [...old.items];
    items[index] = updated;
    return { ...old, items };
  };
}

export function useUpdateComment(options: UseUpdateCommentOptions) {
  const { t } = useTranslation('projects');

  const queryClient = useQueryClient();

  const { projectId, target } = options;

  return useMutation({
    mutationFn: ({ commentId, body }: UpdateCommentArgs) =>
      updateComment(projectId, target, commentId, body),
    onSuccess: (updated) => {
      invalidateCommentAnnotations(queryClient, projectId, target);

      if (!updated.parentId) {
        // Replace the comment in every list cache that holds it (unscoped or
        // any version-scoped variant). The updater is a no-op on caches that
        // don't contain the id, so over-matching is safe.
        queryClient.setQueriesData<InfiniteData<ListCommentsResponseDto, number>>(
          {
            queryKey: PROJECT_COMMENT_QUERY_KEYS.list(
              projectId,
              target.targetType,
              target.targetId,
            ),
            predicate: (q) => isCommentsListKey(q.queryKey),
          },
          replaceCommentInRoots(updated),
        );
        return;
      }

      queryClient.setQueryData<ListCommentsResponseDto>(
        PROJECT_COMMENT_QUERY_KEYS.replies(
          projectId,
          target.targetType,
          target.targetId,
          updated.parentId,
        ),
        replaceCommentInReplies(updated),
      );
    },
    onError: (error) => {
      logger.error('Failed to update comment:', error);
      notificationService.showError(t('commentOnFile.useUpdateComment.failedToUpdateComment'), {
        anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
      });
    },
  });
}
